import type { Engagement, Principle, ProcessStep, TechGroup, WorkItem } from '@typings/content';

/**
 * The stack, grouped by what the work is rather than by popularity. Twelve
 * names in one row reads as a word cloud; three labelled rows read as a stack.
 */
export const TECH_GROUPS: readonly TechGroup[] = [
  { label: 'Web', items: ['React', 'TypeScript', 'Laravel', 'Node.js', 'SASS'] },
  { label: 'Apps', items: ['Flutter', 'Android', '.NET', 'C#'] },
  { label: 'Platform', items: ['AI', 'AWS', 'Docker', 'Kubernetes', 'APIs'] },
];

export const PROCESS_STEPS: readonly ProcessStep[] = [
  {
    title: 'Discover',
    description:
      'We learn the problem, the people and the constraints, then agree what success looks like before anything is built.',
  },
  {
    title: 'Design',
    description:
      'Architecture and interface are designed together, and tested with real users while changes are still cheap.',
  },
  {
    title: 'Build',
    description:
      'Short cycles with working software at the end of each one. You see progress weekly, not at the deadline.',
  },
  {
    title: 'Operate',
    description:
      'Launch is the start. We monitor, maintain and improve what we ship for as long as you need us.',
  },
];

export const PRINCIPLES: readonly Principle[] = [
  {
    title: 'Clarity over cleverness',
    description:
      'Code is read far more than it is written. We optimise for the engineer who maintains it next year.',
  },
  {
    title: 'Measure, then decide',
    description:
      'Performance budgets, real-user metrics and evaluation sets. Opinions are a starting point, not a verdict.',
  },
  {
    title: 'Ship in small steps',
    description:
      'Frequent, reversible releases keep risk low and feedback fast. Big-bang launches are a last resort.',
  },
  {
    title: 'Own the outcome',
    description:
      'We are accountable for what the software achieves, not just for closing tickets on time.',
  },
];

export const ENGAGEMENTS: readonly Engagement[] = [
  {
    title: 'Project',
    description: 'A defined scope delivered end to end, from discovery to launch.',
    bestFor: 'New products and clearly bounded builds',
  },
  {
    title: 'Dedicated team',
    description: 'Engineers embedded with your team, working to your roadmap.',
    bestFor: 'Scaling capacity on an existing product',
  },
  {
    title: 'Advisory',
    description: 'Architecture reviews, audits and technical due diligence.',
    bestFor: 'Decisions that need a second opinion',
  },
];

/**
 * PLACEHOLDER CONTENT. Replace before launch.
 *
 * These describe the kinds of systems we build, not specific clients: no
 * names, no metrics. A work page is a promise, so every entry here must be
 * swapped for a real, approved case study (or removed) before going live.
 */
export const WORK_ITEMS: readonly WorkItem[] = [
  {
    id: 'logistics',
    title: 'Fleet operations platform',
    sector: 'Logistics',
    summary:
      'Live tracking, dispatch and driver apps unified in one platform, replacing a patchwork of spreadsheets.',
    stack: ['Laravel', 'React', 'Flutter', 'AWS'],
    hues: ['#5b8cff', '#6ef2ff'],
  },
  {
    id: 'health',
    title: 'Patient intake assistant',
    sector: 'Healthcare',
    summary:
      'A language-model assistant that drafts intake summaries for clinicians, with every answer traced to its source.',
    stack: ['Python', 'LLM APIs', 'React', 'Azure'],
    hues: ['#a78bfa', '#f472b6'],
  },
  {
    id: 'retail',
    title: 'Headless commerce rebuild',
    sector: 'Retail',
    summary:
      'A storefront rebuilt for speed on mobile networks, on a headless back end the content team can run alone.',
    stack: ['React', 'Node.js', 'GraphQL', 'Docker'],
    hues: ['#fb923c', '#facc15'],
  },
  {
    id: 'finance',
    title: 'Back-office automation',
    sector: 'Financial services',
    summary:
      'Reconciliation and reporting workflows automated end to end, with an audit trail for every decision.',
    stack: ['.NET', 'C#', 'SQL Server', 'Azure'],
    hues: ['#34d399', '#5b8cff'],
  },
];
